/**
 * FresherPlusTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 96 / 100 — ATS Safe
 * Style: Balanced — education + skills + projects — ideal for 0-2 yrs exp
 * Best For: First job applications, entry-level, grad roles
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList, SkillsBlock,
  ContactRow, EmptyState, DS,
} from './templateDesignSystem';

function FresherPlusTemplate({
  resumeData,
  accentColor = '#059669',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, extraList, languageList } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const FSection = ({ title }) => (
    <div style={{ marginTop: 12, marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 18, height: 2, background: accentColor, borderRadius: 1 }} />
        <h2 style={{ fontSize: fs(DS.fs.section), fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.1px', color: '#1a1a1a', margin: 0, fontFamily: 'inherit' }}>{title}</h2>
        <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
      </div>
    </div>
  );

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: fs(DS.fs.body), lineHeight: 1.5, padding: `${DS.sp.pageY} ${DS.sp.pageX}`, boxSizing: 'border-box' }}>

      {/* ─── Header ─── */}
      <header style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: `2.5px solid ${accentColor}`, paddingBottom: 8 }}>
          <div>
            <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
              style={{ fontSize: fs('22px'), fontWeight: 900, color: '#0f172a', margin: 0 }} placeholder="Your Name" />
            {(role || editMode) && (
              <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
                style={{ fontSize: fs('10.5px'), fontWeight: 600, color: accentColor, margin: '2px 0 0' }} placeholder="Graduate | Entry-Level Role" />
            )}
          </div>
        </div>
        <div style={{ marginTop: 5 }}>
          <ContactRow data={d} accentColor={accentColor} />
        </div>
      </header>

      {/* ─── Summary ─── */}
      {(summaryText || editMode) && (
        <section data-ats="summary" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Profile" />
          <EditableField editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
            tag="p" multiline style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.65, margin: 0 }} placeholder="Brief profile..." />
        </section>
      )}

      {/* ─── Education ─── */}
      {educationList.length > 0 && (
        <section data-ats="education" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Education" />
          {educationList.map((edu, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                <p style={{ fontSize: fs(DS.fs.body), color: '#6B7280', margin: '1px 0 0' }}>{edu?.college}</p>
                {edu?.cgpa && <p style={{ fontSize: fs(DS.fs.small), color: accentColor, fontWeight: 700, margin: 0 }}>CGPA / Percentage: {edu.cgpa}</p>}
              </div>
              <EditableField editMode={editMode} value={edu?.year || ''} onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
                tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 8 }} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Skills ─── */}
      {skills.length > 0 && (
        <section data-ats="skills" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Skills" />
          <SkillsBlock skills={skills} accentColor={accentColor} layout="rows" />
        </section>
      )}

      {/* ─── Internships ─── */}
      {internships.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Internships" />
          {internships.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a', margin: 0, display: 'inline' }} />
                  {exp.company && <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' — '}{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Projects ─── */}
      {projects.length > 0 && (
        <section data-ats="projects" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Projects" />
          {projects.map((proj, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a' }}>{proj.title || proj.name}</span>
                  {proj.tech?.length > 0 && (
                    <span style={{ fontSize: fs(DS.fs.small), color: accentColor, marginLeft: 8, fontStyle: 'italic' }}>
                      [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                    </span>
                  )}
                </div>
                {proj.impact && <span style={{ fontSize: fs(DS.fs.small), color: '#059669', fontWeight: 600 }}>{proj.impact}</span>}
              </div>
              <BulletList points={proj.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Experience ─── */}
      {experience.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Work Experience" />
          {experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a', margin: 0, display: 'inline' }} />
                  {exp.company && <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' — '}{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {certList.length > 0 && (
        <section data-ats="certifications" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Certifications" />
          <BulletList points={certList} accentColor={accentColor} />
        </section>
      )}
      {achieveList.length > 0 && (
        <section data-ats="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Achievements" />
          <BulletList points={achieveList} accentColor={accentColor} />
        </section>
      )}
      {extraList.length > 0 && (
        <section data-ats="extracurricular" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Extracurricular" />
          <BulletList points={extraList} accentColor={accentColor} />
        </section>
      )}
      {languageList.length > 0 && (
        <section data-ats="languages" style={{ marginBottom: DS.sp.sectionMb }}>
          <FSection title="Languages" />
          <p style={{ fontSize: fs(DS.fs.body), color: '#374151', margin: 0 }}>{languageList.join('   ·   ')}</p>
        </section>
      )}
    </div>
  );
}

export default FresherPlusTemplate;
